import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  TrendingUp, Flame, ChevronDown, X,
  MessageCircle, Sparkles, Hash
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  AA01: {
    scene: '🏪 편의점. 아르바이트 첫날, 물건 가격을 정리해야 한다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '사과 한 개가 a원이면, 3개는 얼마예요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '사장님', text: '3×a원이지! 근데 수학에서는 ×를 생략하고 그냥 3a라고 써요.' },
      { speaker: 'friend', emoji: '🤔', name: '소연', text: '문자 a가 모르는 값을 대신하는 거구나. 편리하다!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '사장님', text: '맞아요! 사과 a개, 배 b개면 총 개수는 a+b. 이렇게 문자로 수량을 표현할 수 있어요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '문자: 정해지지 않은 수를 문자(a,b,x,y...)로 나타낸다. 수와 문자, 문자와 문자 사이 ×는 생략!' },
    ],
    reveal: '문자는 "아직 모르는 수"를 나타내는 도구 | 3×a = 3a | a×b = ab',
    revealEmoji: '🔤',
  },
  AA02: {
    scene: '📐 수학 시간. 기호 생략 규칙을 배우는 날.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '수학에서 곱셈·나눗셈 기호를 생략하는 규칙이 있어요. a×3을 어떻게 쓸까요?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '3a요? 아, 숫자가 앞에 오는군요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 숫자×문자면 숫자가 앞. 그리고 a÷3은 a/3으로 분수 형태로 써요.' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '1×a = a, -1×a = -a로도 1은 생략하는군요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 1a는 그냥 a, -1a는 -a. 계수가 1이면 생략합니다.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '생략 규칙: 숫자는 문자 앞 | ×생략 | ÷는 분수로 | 1은 생략 | 알파벳 순서' },
    ],
    reveal: 'a×3=3a | 1×a=a | -1×a=-a | a÷3=a/3 | a×b=ab (알파벳순)',
    revealEmoji: '✏️',
  },
  AA03: {
    scene: '🧮 식의 값을 계산하는 수학 퀴즈 대회.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '문제! x=3일 때 2x+1의 값을 구하세요.' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: 'x에 3을 대입하면... 2×3+1 = 6+1 = 7!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽! 이렇게 문자에 수를 넣는 걸 "대입"이라 해요.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: 'a=2, b=-1일 때 3a-b는요? 3×2-(-1) = 6+1 = 7이네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '음수 대입할 때 괄호 꼭 쓰세요! b=-1이면 -b = -(-1) = 1. 부호 실수가 가장 많아요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '대입: 문자에 수를 넣어 계산. 음수 대입 시 반드시 괄호! 순서: 대입 → 계산' },
    ],
    reveal: '대입: 문자에 수를 넣기 | 음수는 괄호 필수 | x=2 → 3x-1 = 3(2)-1 = 5',
    revealEmoji: '🧮',
  },
  AA04: {
    scene: '📦 물건 정리 중. 같은 종류끼리 묶어야 한다.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, 3x+2y+x-y를 어떻게 정리하나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '같은 문자끼리 모아요! x끼리: 3x+x=4x, y끼리: 2y-y=y → 4x+y!' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '마치 3사과+2바나나+1사과-1바나나 = 4사과+1바나나 같네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽한 비유예요! 같은 문자·같은 차수를 "동류항"이라 해요. 동류항끼리만 더할 수 있어요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '동류항: 문자와 차수가 같은 항. 3x와 x → 동류항 ✓ | 3x와 3x² → 동류항 아님 ✗' },
    ],
    reveal: '동류항: 문자+차수 같은 항 | 3x+2x=(3+2)x=5x | 2x+3y는 정리 불가',
    revealEmoji: '📦',
  },
  AA05: {
    scene: '➕ 일차식을 더하고 빼는 법을 연습하는 날.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '(3x+2)+(x-5)는 어떻게 계산해요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '괄호를 풀고 동류항끼리 모아요! 3x+2+x-5 = (3x+x)+(2-5) = 4x-3.' },
      { speaker: 'friend', emoji: '🤔', name: '소연', text: '뺄셈은요? (3x+2)-(x-5)는?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '빼기 뒤 괄호를 풀 때 부호가 바뀌어요! 3x+2-x+5 = 2x+7. "-"가 분배되면 안의 부호가 반전!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '덧셈: 괄호 그대로 해제 | 뺄셈: 괄호 안 부호 전부 반전 후 동류항 정리' },
    ],
    reveal: '(A+B)+(C+D) = A+B+C+D | (A+B)-(C+D) = A+B-C-D (부호 반전!)',
    revealEmoji: '➕',
  },
  AA06: {
    scene: '✖️ 숫자와 식을 곱하고 나누는 계산을 배우는 날.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '3(2x-4)는 어떻게 풀어요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '분배법칙! 3이 괄호 안 모두에 곱해져요. 3×2x-3×4 = 6x-12.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '(6x-12)÷3은요? 각각 나누면 2x-4 다시 나오네요!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 나눗셈은 역수 곱셈으로. (6x-12)÷3 = (6x-12)×(1/3) = 2x-4.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '분배법칙: a(b+c)=ab+ac | 나눗셈: (ax+b)÷c = (a/c)x+(b/c)' },
    ],
    reveal: 'a(b+c) = ab+ac | (ax+b)÷c = ax/c + b/c | -2(3x-1) = -6x+2',
    revealEmoji: '✖️',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'AA01',
    title: '문자를 사용한 식',
    subtitle: '문자로 수량을 표현하는 방법을 이해한다',
    icon: '🔤',
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['문자식', '변수', '수량 표현'],
    objective: '문자를 사용하여 수량과 관계를 식으로 나타낼 수 있다.',
    concept: `**문자를 사용한 식**

📌 왜 문자를 사용할까?
• 정해지지 않은 수를 나타낼 때
• 일반적인 규칙을 표현할 때
• 예) 한 개에 a원인 사과 3개의 값 → 3a원

📌 문자식의 표현 원칙
① 수와 문자의 곱: 수를 문자 앞에 쓰고 × 생략
  • 3×x = 3x,  a×5 = 5a
② 1과 문자의 곱: 1 생략
  • 1×x = x,  -1×x = -x
③ 문자와 문자의 곱: × 생략, 알파벳 순서
  • a×b = ab,  b×a = ab
④ 나눗셈: 분수 형태로
  • x÷3 = x/3,  a÷b = a/b

📌 실생활 예시
| 상황 | 식 |
|------|-----|
| 한 개 a원, 5개 구입 | 5a원 |
| 속력 v, 시간 t → 거리 | vt km |
| 반지름 r인 원의 넓이 | πr² |
| 한 변 a인 정사각형 둘레 | 4a |

⚠️ 주의
• 3+x는 3x가 아님! (덧셈은 생략 불가)
• x÷3과 3÷x는 다름! (x/3 vs 3/x)`,
    keyPoints: [
      '수×문자: 수를 앞에, × 생략 (3×x = 3x)',
      '1×문자: 1 생략 (1×a = a)',
      '문자÷수: 분수 형태 (a÷3 = a/3)',
      '문자+문자: 생략 불가 (a+b는 그대로)',
    ],
    examples: [
      {
        question: '다음을 문자식으로 나타내시오.\n① 한 개에 x원인 귤 4개의 값\n② 시속 60km로 t시간 달린 거리\n③ a를 5로 나눈 몫',
        answer: '① 4x원  ② 60t km  ③ a/5',
        explanation: '① 4×x = 4x\n② 60×t = 60t\n③ a÷5 = a/5',
      },
      {
        question: '다음 중 문자식의 표현이 올바른 것을 고르시오.\n① a×(-3) = -3a  ② 1×b = 1b  ③ x÷y = xy',
        answer: '① -3a만 올바름',
        explanation: '① a×(-3) = -3a ✓ (음수를 앞으로)\n② 1×b = b (1은 생략)\n③ x÷y = x/y (분수 형태)',
      },
    ],
    practice: {
      question: '다음을 문자식으로 나타내시오.\n① x의 3배에서 2를 뺀 수\n② a와 b의 곱을 c로 나눈 수\n③ 정가 p원에서 10% 할인한 가격',
      answer: '① 3x-2  ② ab/c  ③ 0.9p원 (또는 p - 0.1p)',
      hint: '① 3배=×3, 뺀다=-, ② 곱=×, 나눈다=÷→분수, ③ 10% 할인 = ×0.9',
      steps: [
        '① x의 3배: 3x, 2를 뺀다: 3x-2',
        '② a×b÷c = ab/c',
        '③ 10% 할인 → 90% 남음 → p×0.9 = 0.9p',
      ],
    },
    misconceptions: ['AM01', 'AM02'],
  },
  {
    id: 'AA02',
    title: '곱셈·나눗셈 기호의 생략',
    subtitle: '기호 생략 규칙을 완전히 익혀 문자식을 바르게 쓴다',
    icon: '✏️',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['기호 생략', '계수', '문자식 규칙'],
    objective: '곱셈·나눗셈 기호의 생략 규칙을 이해하고 올바르게 문자식을 표현한다.',
    concept: `**기호 생략의 5가지 규칙**

📌 규칙 1: 수×문자 → 수를 앞에, × 생략
• 2×a = 2a
• (-3)×x = -3x
• 0.5×y = 0.5y

📌 규칙 2: 1×문자 → 1 생략
• 1×a = a  (1a ✗)
• (-1)×x = -x  (-1x ✗)

📌 규칙 3: 문자×문자 → × 생략, 알파벳 순서
• a×b = ab (ba도 가능하지만 관례상 ab)
• x×y×z = xyz

📌 규칙 4: 나눗셈 → 분수
• a÷3 = a/3
• (2x+1)÷5 = (2x+1)/5

📌 규칙 5: 같은 문자 반복 → 거듭제곱
• a×a = a²
• x×x×x = x³

⚠️ 자주 틀리는 것
• 2×3×a = 6a (수끼리 먼저 계산!)
• a×b×a = a²b (같은 문자는 거듭제곱)
• 3+a ≠ 3a (덧셈은 생략 불가!)`,
    keyPoints: [
      '수는 문자 앞, × 생략',
      '계수 1은 생략 (1a→a, -1a→-a)',
      '나눗셈은 분수로',
      '같은 문자 반복은 거듭제곱 (a×a=a²)',
    ],
    examples: [
      {
        question: '다음을 기호를 생략한 식으로 나타내시오.\n① (-5)×x  ② a×a×b  ③ (x+2)÷4  ④ 1×y×(-1)',
        answer: '① -5x  ② a²b  ③ (x+2)/4  ④ -y',
        explanation: '① (-5)×x = -5x\n② a×a×b = a²b\n③ (x+2)÷4 = (x+2)/4\n④ 1×y×(-1) = -1×y = -y',
      },
      {
        question: '다음 중 옳지 않은 것은?\n① 3×a = 3a  ② a×1 = 1a  ③ b÷2 = b/2  ④ x×(-2) = -2x',
        answer: '② 1a → a로 써야 함 (1 생략)',
        explanation: '② a×1 = 1×a = a (계수 1은 생략)\n나머지는 모두 올바른 표현',
      },
    ],
    practice: {
      question: '다음을 기호를 생략하여 나타내시오.\n① 2×x×x×(-3)\n② a÷b×c\n③ (x-3)÷(-2)',
      answer: '① -6x²  ② ac/b  ③ -(x-3)/2 또는 (x-3)/(-2)',
      hint: '① 수끼리 먼저: 2×(-3)=-6, x×x=x², ② a×c÷b = ac/b, ③ 나눗셈→분수',
      steps: [
        '① 수끼리: 2×(-3)=-6, 문자: x×x=x² → -6x²',
        '② a÷b×c = a/b×c = ac/b',
        '③ (x-3)÷(-2) = (x-3)/(-2) = -(x-3)/2',
      ],
    },
    misconceptions: ['AM01', 'AM03'],
  },
  {
    id: 'AA03',
    title: '식의 값 구하기',
    subtitle: '문자에 수를 대입하여 식의 값을 계산한다',
    icon: '🧮',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    textColor: 'text-emerald-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['대입', '식의 값', '계산'],
    objective: '문자에 수를 대입하여 식의 값을 올바르게 계산할 수 있다.',
    concept: `**식의 값 구하기 (대입)**

📌 대입이란?
문자에 특정 수를 넣어 계산하는 것

📌 대입 순서
① 식에서 문자를 찾는다
② 문자에 주어진 수를 괄호로 묶어 대입
③ 순서대로 계산

📌 예시: x=3일 때 2x²-x+1의 값
① x 자리에 (3) 대입
② 2×(3)²-(3)+1
③ = 2×9-3+1 = 18-3+1 = 16

📌 음수 대입 — 가장 중요!
x=-2일 때 x²-3x의 값:
• x²: (-2)² = 4  (괄호 필수!)
• -3x: -3×(-2) = 6
• 합: 4+6 = 10

⚠️ 절대 하면 안 되는 실수
• x=-2일 때 x² = -2² = -4  ← 틀림!
• x=-2일 때 x² = (-2)² = 4  ← 맞음!
• 음수는 항상 괄호로 감싸서 대입!`,
    keyPoints: [
      '문자 → 수로 교체할 때 괄호 사용',
      '음수 대입 시 괄호 필수!',
      '계산 순서: 지수 → 곱셈/나눗셈 → 덧셈/뺄셈',
      '식의 값: 대입 후 나오는 숫자',
    ],
    examples: [
      {
        question: 'x=2, y=-3일 때 다음을 구하시오.\n① 3x-y  ② x²+2y',
        answer: '① 9  ② -2',
        explanation: '① 3×(2)-(-3) = 6+3 = 9\n② (2)²+2×(-3) = 4-6 = -2',
      },
      {
        question: 'a=-1, b=2일 때 2a²-3b+1의 값은?',
        answer: '2×(-1)²-3×2+1 = 2-6+1 = -3',
        explanation: 'a=-1 → (-1)²=1 (괄호 중요!)\n2×1-3×2+1 = 2-6+1 = -3',
      },
    ],
    practice: {
      question: 'x=-2, y=3일 때 다음을 구하시오.\n① x²-2x+3\n② (x+y)/x',
      answer: '① 11  ② -1/2',
      hint: '① (-2)²=4, -2×(-2)=4, ② ((-2)+3)/(-2)=1/(-2)',
      steps: [
        '① (-2)²-2×(-2)+3 = 4+4+3 = 11',
        '② ((-2)+(3))/(-2) = 1/(-2) = -1/2',
      ],
    },
    misconceptions: ['AM04', 'AM05'],
  },
  {
    id: 'AA04',
    title: '일차식과 동류항',
    subtitle: '동류항의 개념을 이해하고 동류항끼리 모아 정리한다',
    icon: '📦',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    textColor: 'text-orange-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['동류항', '일차식', '항의 정리'],
    objective: '동류항의 개념을 이해하고 동류항끼리 모아 식을 간단히 정리할 수 있다.',
    concept: `**일차식과 동류항**

📌 용어 정리
• 항: 수 또는 문자의 곱으로 이루어진 식의 단위
  - 3x²+2x-1에서 항: 3x², 2x, -1
• 계수: 항에서 문자 앞의 수 (3x²의 계수 = 3)
• 상수항: 문자가 없는 항 (위에서 -1)
• 차수: 문자가 곱해진 횟수 (3x²의 차수 = 2)
• 일차식: 최고 차수가 1인 식 (3x+2, a-5)

📌 동류항
문자와 차수가 모두 같은 항

| 항 | 동류항 여부 |
|----|------------|
| 3x와 5x | ✅ 동류항 (x, 1차) |
| 2x와 2x² | ❌ 차수 다름 |
| 3a와 3b | ❌ 문자 다름 |
| 4와 -7 | ✅ 동류항 (상수항) |

📌 동류항 정리 방법
• 계수끼리 더하고 문자는 그대로
• 3x+5x = (3+5)x = 8x
• 4a-a = (4-1)a = 3a  ← a는 1a!

⚠️ 주의
• 2x+3y는 정리 불가 (동류항 없음)
• 5x²+2x는 정리 불가 (차수 다름)`,
    keyPoints: [
      '동류항: 문자+차수 모두 같은 항',
      '동류항끼리만 덧셈·뺄셈 가능',
      '계수끼리 더하고 문자는 그대로',
      '상수항끼리도 동류항',
    ],
    examples: [
      {
        question: '다음 식을 동류항끼리 모아 간단히 하시오.\n① 5x-3+2x+7  ② 3a-2b+a+5b',
        answer: '① 7x+4  ② 4a+3b',
        explanation: '① x항: 5x+2x=7x, 상수항: -3+7=4 → 7x+4\n② a항: 3a+a=4a, b항: -2b+5b=3b → 4a+3b',
      },
      {
        question: '다음 중 3x²-5x+2에서 x의 계수는?',
        answer: '-5',
        explanation: '3x²-5x+2에서 x¹의 계수는 -5\n(x² 앞의 3은 x²의 계수)',
      },
    ],
    practice: {
      question: '다음 식을 간단히 하시오.\n① 2x-4y+3x+y-2\n② a²+3a-2a²+a-5',
      answer: '① 5x-3y-2  ② -a²+4a-5',
      hint: '① x항, y항, 상수항 따로 모으기. ② a²항: 1-2=-1, a항: 3+1=4',
      steps: [
        '① x항: 2x+3x=5x, y항: -4y+y=-3y, 상수항: -2 → 5x-3y-2',
        '② a²항: a²-2a²=-a², a항: 3a+a=4a, 상수항: -5 → -a²+4a-5',
      ],
    },
    misconceptions: ['AM06', 'AM07'],
  },
  {
    id: 'AA05',
    title: '일차식의 덧셈·뺄셈',
    subtitle: '괄호를 해제하고 동류항을 모아 일차식을 계산한다',
    icon: '➕',
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
    textColor: 'text-teal-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['일차식 덧셈', '괄호 해제', '부호 변환'],
    objective: '괄호를 올바르게 해제하고 일차식의 덧셈·뺄셈을 계산할 수 있다.',
    concept: `**일차식의 덧셈·뺄셈**

📌 덧셈: 괄호 앞이 +이면 그대로 해제
• (2x+3)+(x-1)
• = 2x+3+x-1
• = 3x+2

📌 뺄셈: 괄호 앞이 -이면 안의 부호 전부 반전
• (2x+3)-(x-1)
• = 2x+3-x+1  ← -1이 (+1이 됨!)
• = x+4

📌 부호 반전 확인
-(x-1) = -x+1
-(3x+2) = -3x-2
-(-x+4) = x-4

📌 계산 순서
① 괄호 해제 (뺄셈이면 부호 반전)
② 동류항끼리 모으기
③ 계산

📌 예시: (3x-2)-(2x+5)+(x-1)
= 3x-2-2x-5+x-1
= (3x-2x+x)+(-2-5-1)
= 2x-8

⚠️ 가장 흔한 실수
• -(2x-3) = -2x-3  ← 틀림!
• -(2x-3) = -2x+3  ← 맞음! (-3→+3)`,
    keyPoints: [
      '+(괄호): 부호 그대로',
      '-(괄호): 안의 모든 부호 반전',
      '괄호 해제 후 동류항 모으기',
      '-(ax+b) = -ax-b, -(ax-b) = -ax+b',
    ],
    examples: [
      {
        question: '다음을 계산하시오.\n① (4x-3)+(2x+5)\n② (4x-3)-(2x+5)',
        answer: '① 6x+2  ② 2x-8',
        explanation: '① 4x-3+2x+5 = 6x+2\n② 4x-3-2x-5 = 2x-8 (괄호 앞 -로 부호 반전)',
      },
      {
        question: '(3a-b)-(a+2b)+(2a-3b)를 간단히 하시오.',
        answer: '4a-6b',
        explanation: '3a-b-a-2b+2a-3b = (3-1+2)a+(-1-2-3)b = 4a-6b',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① (5x-2)-(3x-4)+(-x+1)\n② 3(x+2)-2(x-1)',
      answer: '① x+3  ② x+8',
      hint: '① 괄호 해제 후 동류항. ② 분배법칙 먼저: 3x+6, -2x+2',
      steps: [
        '① 5x-2-3x+4-x+1 = (5-3-1)x+(-2+4+1) = x+3',
        '② 3x+6-2x+2 = x+8',
      ],
    },
    misconceptions: ['AM08', 'AM09'],
  },
  {
    id: 'AA06',
    title: '일차식의 곱셈·나눗셈',
    subtitle: '분배법칙으로 일차식과 수의 곱셈·나눗셈을 계산한다',
    icon: '✖️',
    color: 'bg-pink-500',
    lightColor: 'bg-pink-50',
    borderColor: 'border-pink-300',
    textColor: 'text-pink-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['분배법칙', '곱셈', '나눗셈'],
    objective: '분배법칙을 사용하여 일차식과 수의 곱셈·나눗셈을 계산할 수 있다.',
    concept: `**일차식의 곱셈·나눗셈**

📌 분배법칙
a(b+c) = ab+ac
(b+c)×a = ba+ca

📌 수×일차식
3(2x-5) = 3×2x - 3×5 = 6x-15
-2(x+3) = -2x-6
½(4x-2) = 2x-1

📌 일차식×수 (순서 바꿔도 같음)
(3x+1)×4 = 12x+4

📌 일차식÷수 (역수 곱셈으로)
(6x-4)÷2 = (6x-4)×½ = 3x-2
(3x+9)÷3 = x+3

📌 음수 곱할 때 주의!
-3(x-2) = -3x+6  (- × - = +)
-½(2x-4) = -x+2

📌 종합 계산
2(3x-1)+3(x+2)
= 6x-2+3x+6
= 9x+4

⚠️ 분배할 때 모든 항에 곱해야!
3(x-2) = 3x-6  ← 맞음
3(x-2) = 3x-2  ← 틀림! (-2에도 3 곱해야)`,
    keyPoints: [
      'a(b+c) = ab+ac (분배법칙)',
      '음수×음수 = 양수 (부호 주의)',
      '나눗셈 → 역수 곱셈으로 변환',
      '모든 항에 빠짐없이 분배!',
    ],
    examples: [
      {
        question: '다음을 계산하시오.\n① -3(2x-4)  ② (6x-9)÷3  ③ 2(x+1)-3(x-2)',
        answer: '① -6x+12  ② 2x-3  ③ -x+8',
        explanation: '① -3×2x-(-3)×4 = -6x+12\n② (6x-9)×(1/3) = 2x-3\n③ 2x+2-3x+6 = -x+8',
      },
      {
        question: '½(4x+6)-¼(8x-4)를 계산하시오.',
        answer: '0',
        explanation: '½(4x+6) = 2x+3\n¼(8x-4) = 2x-1\n(2x+3)-(2x-1) = 2x+3-2x+1 = 4\n※ 답 재확인: 2x+3-2x+1 = 4',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① 4(2x-3)-2(3x-5)\n② (10x-5)÷(-5)\n③ -2(x+3)+3(2x-1)',
      answer: '① 2x-2  ② -2x+1  ③ 4x-9',
      hint: '① 8x-12-6x+10, ② ÷(-5)=×(-1/5), ③ -2x-6+6x-3',
      steps: [
        '① 8x-12-6x+10 = 2x-2',
        '② (10x-5)÷(-5) = -2x+1',
        '③ -2x-6+6x-3 = 4x-9',
      ],
    },
    misconceptions: ['AM10', 'AM11'],
  },
]

const TOTAL_LEARN_STEPS = STEPS.length
const UNIT_ID = 'A1-1'

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function PracticeBox({ practice, stepId }: { practice: typeof STEPS[0]['practice'], stepId: string }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-violet-100 text-violet-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-violet-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      {!revealed ? (
        <div className="flex gap-2">
          {lineIdx < story.lines.length - 1 && (
            <button onClick={() => setLineIdx(lineIdx + 1)}
              className="flex-1 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 font-semibold rounded-xl text-xs transition-colors">
              다음 대화 →
            </button>
          )}
          {lineIdx === story.lines.length - 1 && (
            <button onClick={() => setRevealed(true)}
              className="flex-1 py-2 bg-violet-500 hover:bg-violet-600 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1">
              <Sparkles size={12} /> 핵심 공식 보기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
          <p className="text-lg text-center mb-1">{story.revealEmoji}</p>
          <p className="text-xs text-violet-800 font-mono text-center">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id)).slice(0, 3)
  if (visible.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={12} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X size={11} /></button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
interface A1LearnPageProps { defaultStepId?: string | null }

export default function A1LearnPage({ defaultStepId }: A1LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(null)
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')

  const { completeStep, markPracticeAnswered } = useProgressStore(s => ({
    completeStep: s.completeStep,
    markPracticeAnswered: s.markPracticeAnswered,
  }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  // ── 단계 목록 화면 ──────────────────────────────────────────────────────────
  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <PredictedFragmentPanel fragments={predictedFragments} />

        {/* 헤더 */}
        <div className="card bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center text-2xl">🔤</div>
            <div>
              <h2 className="text-lg font-black text-slate-800">문자의 사용과 식</h2>
              <p className="text-xs text-slate-500">A1-1 · 문자와 식 영역</p>
            </div>
          </div>
          {/* 진도 바 */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-violet-100 rounded-full overflow-hidden">
              <div className="h-2 bg-violet-500 rounded-full transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs font-bold text-violet-600 w-16 text-right">
              {completedCount}/{TOTAL_LEARN_STEPS} 완료
            </span>
          </div>
        </div>

        {/* 단계 목록 */}
        <div className="space-y-2">
          {STEPS.map((step, idx) => {
            const stepProg = learnProgress.steps[step.id]
            const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${done ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 hover:border-violet-300'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${done ? 'bg-emerald-100' : step.lightColor}`}>
                    {done ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                          <Youtube size={9} /> 영상
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{step.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-400">{step.id}</span>
                    <ChevronRight size={15} className="text-slate-300" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* 완료 배너 */}
        {completedCount === TOTAL_LEARN_STEPS && (
          <div className="card bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-sm font-bold text-emerald-700">모든 개념 학습 완료!</p>
            <p className="text-xs text-slate-500 mt-1">실전 TEST에 도전해보세요</p>
          </div>
        )}
      </div>
    )
  }

  // ── 단계 상세 화면 ──────────────────────────────────────────────────────────
  const step = selectedStep
  const stepProg = learnProgress.steps[step.id]
  const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
  const story = STEP_STORIES[step.id]
  const video = getApprovedByTag(step.id)

  const tabs = [
    { id: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { id: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { id: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { id: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* 상단 헤더 */}
      <div className={`${step.lightColor} border-b ${step.borderColor} px-4 pt-3 pb-0 flex-shrink-0`}>
        <button onClick={() => setSelectedStep(null)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2 transition-colors">
          ← 목록으로
        </button>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{step.icon}</span>
          <div>
            <h2 className="text-base font-black text-slate-800">{step.title}</h2>
            <p className="text-xs text-slate-500">{step.subtitle}</p>
          </div>
          {done && <CheckCircle size={18} className="text-emerald-500 ml-auto" />}
        </div>
        {/* 태그 */}
        <div className="flex gap-1 flex-wrap mb-2">
          {step.tags.map(tag => (
            <span key={tag} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${step.lightColor} ${step.textColor} border ${step.borderColor}`}>
              #{tag}
            </span>
          ))}
        </div>
        {/* 탭 */}
        <div className="flex gap-0.5">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-t-xl border-2 transition-all ${
                activeTab === tab.id
                  ? `bg-white ${step.borderColor} border-b-white ${step.textColor}`
                  : 'bg-white/40 border-transparent text-slate-400 hover:bg-white/70'
              }`}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 탭 콘텐츠 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* 영상 버튼 */}
        {video && (
          <button onClick={() => setVideoModal({ stepId: step.id, label: step.title })}
            className="w-full flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors">
            <Youtube size={14} className="text-red-500" /> 개념 영상 보기 · {step.title}
          </button>
        )}

        {/* 스토리 탭 */}
        {activeTab === 'story' && story && <StoryCard story={story} />}

        {/* 개념 탭 */}
        {activeTab === 'concept' && (
          <div className="space-y-3">
            <div className={`${step.lightColor} border ${step.borderColor} rounded-2xl p-4`}>
              <p className={`text-xs font-bold ${step.textColor} mb-1 flex items-center gap-1`}>
                <Target size={12} /> 학습 목표
              </p>
              <p className="text-sm text-slate-700">{step.objective}</p>
            </div>
            <div className="card prose prose-sm max-w-none">
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                {step.concept}
              </pre>
            </div>
            <div className={`border ${step.borderColor} rounded-2xl p-4`}>
              <p className={`text-xs font-bold ${step.textColor} mb-2 flex items-center gap-1`}>
                <Star size={12} /> 핵심 포인트
              </p>
              <div className="space-y-1.5">
                {step.keyPoints.map((kp, i) => (
                  <div key={i} className="flex gap-2 text-xs text-slate-700">
                    <span className={`font-black ${step.textColor} flex-shrink-0`}>✓</span>
                    <span>{kp}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 예제 탭 */}
        {activeTab === 'examples' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500 font-medium">풀어보고 풀이를 확인하세요.</p>
            {step.examples.map((ex, i) => (
              <ExampleCard key={i} example={ex} idx={i} />
            ))}
          </div>
        )}

        {/* 연습 탭 */}
        {activeTab === 'practice' && (
          <div className="space-y-3">
            <PracticeBox practice={step.practice} stepId={step.id} />
            {/* 완료 버튼 */}
            {!done && (
              <button onClick={() => handleComplete(step)}
                className={`w-full py-3 ${step.color} hover:opacity-90 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2`}>
                <CheckCircle size={17} /> 학습 완료!
              </button>
            )}
            {done && (
              <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-2xl">
                <CheckCircle size={16} className="text-emerald-500" />
                <span className="text-sm font-bold text-emerald-600">완료된 단계입니다</span>
              </div>
            )}
          </div>
        )}

        {/* 오개념 경고 */}
        {activeTab === 'concept' && step.misconceptions.length > 0 && (
          <div className="border border-orange-200 bg-orange-50 rounded-2xl p-3">
            <p className="text-xs font-bold text-orange-600 mb-1.5 flex items-center gap-1">
              <AlertTriangle size={12} /> 자주 틀리는 오개념
            </p>
            {step.misconceptions.map(code => (
              <p key={code} className="text-xs text-orange-700 bg-orange-100 rounded-lg px-2 py-1 mb-1">
                [{code}] 자주 하는 실수 — AI 튜터 탭에서 자세히 확인
              </p>
            ))}
          </div>
        )}
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
